import * as React from 'react';
import { sortBy, reverse } from 'lodash';
import { InjectedRouter } from 'react-router';

// @ts-ignore
import AdminDistrictSisDashboardCleverHeader from 'admin/components/AdminDistrictSisDashboardCleverHeader';
// @ts-ignore
import AdminDistrictSisDashboardSftpHeader from 'admin/components/AdminDistrictSisDashboardSftpHeader';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore
import Divider from 'components/Divider';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
// @ts-ignore
import SmartTable from 'components/SmartTable';
import StatusText from 'components/StatusText';
import TableCell from 'components/TableCell';
import TableRow from 'components/TableRow';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import Time from 'components/Time';

import {
  AdminDistrictSisDashboard_organizations,
  AdminDistrictSisDashboard_organizations_childOrganizations,
} from 'admin/queries/__generated__/AdminDistrictSisDashboard';

import './styles/AdminDistrictSisDashboard.scss';

type Props = {
  organization: AdminDistrictSisDashboard_organizations;
  orgId: number;
  syncMethod: string;
  router: InjectedRouter;
  handleRosteringEnforcementChange: (allowedToLeave: boolean) => void;
};

const nameSorts = [
  { key: 'name_asc', name: 'Ascending' },
  { key: 'name_desc', name: 'Descending' },
];

const errorSorts = [
  { key: 'error_asc', name: 'Ascending' },
  { key: 'error_desc', name: 'Descending' },
];

const alertSorts = [
  { key: 'alert_asc', name: 'Ascending' },
  { key: 'alert_desc', name: 'Descending' },
];

const statusSorts = [
  { key: 'status_asc', name: 'Ascending' },
  { key: 'status_desc', name: 'Descending' },
];

const errorTooltip =
  "Errors are mistakes in your SIS that prevent student or teacher accounts from being created. It's critical you address these as soon as possible.";
const alertTooltip =
  'Alerts are minor data errors in your SIS that, if fixed, would make Remind more useful and productive to use.';

const COLUMNS = [
  { key: 'name', name: 'School', sorts: nameSorts },
  { key: 'teachers', name: 'Teachers' },
  { key: 'students', name: 'Students' },
  { key: 'errors', name: 'Errors', sorts: errorSorts, tooltip: errorTooltip },
  { key: 'alerts', name: 'Alerts', sorts: alertSorts, tooltip: alertTooltip },
  { key: 'sync_status', name: 'Sync status', sorts: statusSorts },
];

export function sortOrgs(
  orgs: (AdminDistrictSisDashboard_organizations_childOrganizations | null)[],
  sort?: string
): (AdminDistrictSisDashboard_organizations_childOrganizations | null)[] {
  let iteratees: ((
    o: AdminDistrictSisDashboard_organizations_childOrganizations | null
  ) => string | boolean | number | null)[] = [
    o => o && o.sisSyncOverview && o.sisSyncOverview.syncStatus === 'on_hold',
    o => o && o.sisSyncOverview && o.sisSyncOverview.errorCount,
    o => o && o.sisSyncOverview && o.sisSyncOverview.syncStatus !== 'not_set_up',
  ];
  let descending = true;

  if (sort) {
    if (sort.endsWith('_asc')) {
      descending = false;
    }

    if (sort.startsWith('name_')) {
      iteratees = [o => o && o.name];
    } else if (sort.startsWith('error_')) {
      iteratees = [o => o && o.sisSyncOverview && o.sisSyncOverview.errorCount];
    } else if (sort.startsWith('alert_')) {
      iteratees = [o => o && o.sisSyncOverview && o.sisSyncOverview.alertCount];
    } else if (sort.startsWith('status_')) {
      iteratees = [o => o && o.sisSyncOverview && o.sisSyncOverview.syncStatus];
    }
  }

  if (typeof orgs === 'undefined' || orgs === null || orgs.length < 1) {
    return [];
  }

  const sortedOrgs = sortBy<AdminDistrictSisDashboard_organizations_childOrganizations | null>(
    [...orgs],
    iteratees
  );

  return descending ? reverse([...sortedOrgs]) : sortedOrgs;
}

export default class AdminDistrictSisDashboard extends React.Component<Props> {
  renderErrorName = (name: string) => {
    return (
      <div className="AdminDistrictSisDashboard-errorName">
        <TextWithGlyph
          glyph="explanation-circle"
          glyphColor="lava"
          glyphSize="large"
          gutter="xsmall"
          size="xxsmall"
          color="lava"
          weight="semibold">
          {name}
        </TextWithGlyph>
      </div>
    );
  };

  renderSyncStatus = (status: string) => {
    if (status === 'not_set_up') {
      return 'Set up sync';
    }
    return (
      <StatusText
        data-test={`status-text-${status}`}
        success={status === 'connected'}
        failure={status === 'on_hold'}>
        {status.replace('_', ' ')}
      </StatusText>
    );
  };

  renderHeader(organization: AdminDistrictSisDashboard_organizations) {
    const { syncMethod } = this.props;

    const headerTitle = 'SIS sync';
    const headerSubtitle = 'Select a school to view its details.';

    if (syncMethod === 'Clever') {
      return (
        <AdminDistrictSisDashboardCleverHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          organization={organization}
        />
      );
    } else if (syncMethod === 'SFTP') {
      return (
        <AdminDistrictSisDashboardSftpHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          organization={organization}
        />
      );
    } else {
      return null;
    }
  }

  renderLastUploadInfo(organization: AdminDistrictSisDashboard_organizations) {
    if (
      !organization ||
      !organization.sisSyncOverview ||
      !organization.sisSyncOverview.districtStatus
    ) {
      return null;
    }

    const {
      lastUploadTimestamp,
      lastUploadFiles,
      filesZipUrl,
    } = organization.sisSyncOverview.districtStatus;

    if (!lastUploadTimestamp || !lastUploadFiles || lastUploadFiles.length === 0) {
      return null;
    }

    const time = (
      <Time
        date={new Date(lastUploadTimestamp)}
        month="long"
        day="numeric"
        hour12
        hour="numeric"
        minute="2-digit"
      />
    );
    const files = lastUploadFiles.join(', ');

    return (
      <Row size="xsmall" data-test="last-upload-info-row">
        <Divider size="xsmall" />
        <Text>
          Last files received {time}: {files}.{' '}
        </Text>
        <Link weight="semibold" href={filesZipUrl || '#'}>
          Download all CSVs.
        </Link>
      </Row>
    );
  }

  renderRosteringEnforcementCheckbox(organization: AdminDistrictSisDashboard_organizations) {
    const { canRosteredUsersLeaveOrg } = organization.permissions;
    return (
      <Row>
        <Container size="medium" align="left" flush>
          <Checkbox
            data-test="rostering-enforcement-checkbox"
            label="Enable rostering enforcement"
            labelNote={
              <Text color="thunder" weight="normal">
                This removes the option for SIS-linked users to opt out of organization-wide
                messages. <br />
                They can still leave individual classes within the organization.{' '}
                <Link href={LinkConstants.ROSTERING_ENFORCEMENT} target="_blank">
                  Learn more
                </Link>
              </Text>
            }
            checked={!canRosteredUsersLeaveOrg}
            onChange={() => this.props.handleRosteringEnforcementChange(!canRosteredUsersLeaveOrg)}
          />
        </Container>
      </Row>
    );
  }

  renderBody(organization: AdminDistrictSisDashboard_organizations) {
    const { orgId } = this.props;
    if (!CurrentUser.isSignedIn()) {
      return null;
    }

    const currentUser = CurrentUser.getUser();
    return (
      <section className="AdminAdminsView">
        {this.renderHeader(organization)}
        {currentUser.hasFeature('rostering_enforcement_checkbox.web.sept-19') &&
          this.renderRosteringEnforcementCheckbox(organization)}
        {this.renderLastUploadInfo(organization)}
        <SmartTable columns={COLUMNS} namespace={`admin-sis-dashboard-school-list-${orgId}`}>
          {({ sort }: { sort: string }) => {
            return (
              <tbody>
                {sortOrgs(organization.childOrganizations, sort).map(
                  (child: AdminDistrictSisDashboard_organizations_childOrganizations | null) => {
                    if (!child || !child.paidAt) {
                      return null;
                    }
                    const { sisSyncOverview } = child;
                    const childId = child.id || '';
                    const syncStatus = sisSyncOverview ? sisSyncOverview.syncStatus : '';
                    const isOnHold = syncStatus === 'on_hold';
                    const hasErrors = (sisSyncOverview ? sisSyncOverview.errorCount : 0) > 0;
                    const notSetUp = syncStatus === 'not_set_up';
                    const noData = syncStatus === 'processing' || notSetUp;
                    const onRowClick = () => {
                      if (!notSetUp) {
                        this.props.router.push({
                          pathname: `/organizations/${childId}/settings/organization/sis_sync`,
                        });
                      }
                    };
                    const errorCount = sisSyncOverview ? sisSyncOverview.errorCount : null;
                    return (
                      <TableRow hoverable key={child.uuid} onClick={onRowClick}>
                        <td>
                          {hasErrors || isOnHold ? this.renderErrorName(child.name) : child.name}
                        </td>
                        <TableCell empty={noData}>
                          {sisSyncOverview ? sisSyncOverview.teacherCount : null}
                        </TableCell>
                        <TableCell empty={noData}>
                          {sisSyncOverview ? sisSyncOverview.studentCount : null}
                        </TableCell>
                        <TableCell empty={noData}>
                          {hasErrors ? (
                            <Text weight="semibold" color="lava">
                              {errorCount}
                            </Text>
                          ) : (
                            errorCount
                          )}
                        </TableCell>
                        <TableCell empty={noData}>
                          {sisSyncOverview ? sisSyncOverview.alertCount : null}
                        </TableCell>
                        <td>{this.renderSyncStatus(syncStatus || '')}</td>
                      </TableRow>
                    );
                  }
                )}
              </tbody>
            );
          }}
        </SmartTable>
      </section>
    );
  }

  render() {
    return (
      <div className="AdminDistrictSisDashboard">{this.renderBody(this.props.organization)}</div>
    );
  }
}
